import { useMemo } from "react";
import { usePathname, useLocalSearchParams, useGlobalSearchParams } from "expo-router";
import { View, Text } from "@src/components";
import { getWebBreadcrumbs, type WebBreadcrumbItem } from "@src/utils/web-breadcrumbs";
import { NavPressable } from "@src/components/NavPressable";
import { TenantId } from "@visioni/a3s-schema";
import { getTenantDisplayName, useTenant, useTenantId } from "@src/features/tenants";
import { useSites } from "@src/features/cameras/hooks/useSites";

export function Breadcrumbs() {
  const pathname = usePathname();
  const contextTenantId = useTenantId();
  const params = useLocalSearchParams<{
    tenantId?: string;
    siteId?: string;
    siteIds?: string;
    cameraKey?: string;
    // Traffic-flow configure page passes the site as a `site` query param.
    site?: string;
  }>();
  const globalParams = useGlobalSearchParams<{ action?: string }>();

  const siteId = params.site ?? params.siteId;
  const { sites } = useSites({ enabled: !!siteId });
  const siteName = useMemo(() => sites?.find((s) => String(s.siteId) === siteId)?.siteName, [sites, siteId]);

  const tenantId = params.tenantId || contextTenantId || undefined;
  const { tenants } = useTenant();
  const tenant = tenants.find((t) => TenantId.toString(t.id) === tenantId);

  const breadcrumbs = getWebBreadcrumbs({
    pathname,
    tenantId,
    siteId: siteId ?? params.siteIds,
    cameraKey: params.cameraKey,
    siteName,
    ...(tenant !== undefined && { tenantName: getTenantDisplayName(tenant) }),
    action: globalParams.action,
  });

  return (
    <View flex={1} flexDirection="row" alignItems="center" gap="md" style={{ minWidth: 0 }}>
      {breadcrumbs.map((item, index) => (
        <View key={index} flexDirection="row" alignItems="center" gap="md" style={{ flexShrink: 1, minWidth: 0 }}>
          {index > 0 && (
            <Text textStyle="sm/regular" color="textMuted">
              /
            </Text>
          )}
          {item.href ?
            <NavPressable href={item.href as never} style={{ flexShrink: 1, minWidth: 0 }}>
              <CrumbLabel item={item} textStyle="sm/regular" />
            </NavPressable>
          : <CrumbLabel item={item} textStyle="sm/bold" />}
        </View>
      ))}
    </View>
  );
}

function CrumbLabel({ item, textStyle }: { item: WebBreadcrumbItem; textStyle: "sm/regular" | "sm/bold" }) {
  if (item.scope === undefined) {
    return (
      <Text textStyle={textStyle} color="text" lineClamp={1}>
        {item.label}
      </Text>
    );
  }
  // A long scope, such as an organisation's name, shortens; the page it scopes stays whole.
  return (
    <View flexDirection="row" style={{ flexShrink: 1, minWidth: 0 }}>
      <Text textStyle={textStyle} color="text" lineClamp={1} style={{ flexShrink: 1 }}>
        {item.scope}
      </Text>
      <Text textStyle={textStyle} color="text" lineClamp={1} style={{ flexShrink: 0 }}>
        {`: ${item.label}`}
      </Text>
    </View>
  );
}
